// Riyadah — exercise library + helpers.
//
// Curated by hand so units, defaults and muscle tags make sense out of the
// box. Negative weight on assisted machines = kg of assistance (e.g.
// assisted pull-up at -25 means 25kg counterweight is helping you).
//
// Categories: Machines, Free weights, Bodyweight, Cardio, Stretch.
// Units:
//   "kg"        — added external load
//   "kg_assist" — assistance load on assisted-pull-up / assisted-dip machines
//   "reps"      — bodyweight, only reps tracked
//   "min"       — duration only (cardio, stretch)
//
// The user trains 3x/week with a PT in circuits, so defaults assume
// circuit-style sets/reps (~2x10) rather than 5x5 strength programming.

export const CATEGORIES = ["Machines", "Free weights", "Bodyweight", "Cardio", "Stretch"];

export const MUSCLE_GROUPS = ["Chest", "Back", "Shoulders", "Biceps", "Triceps", "Legs", "Glutes", "Core", "Cardio", "Full body"];

export const EXERCISE_LIBRARY = [
  // ───────── Machines ─────────
  { id: "assisted_pullup",   name: "Assisted Pull-up",       category: "Machines", primary: "Back",      secondary: ["Biceps"],         unit: "kg_assist", defaults: { sets: 2, reps: 10, weight: -25 } },
  { id: "assisted_dip",      name: "Assisted Dip",           category: "Machines", primary: "Triceps",   secondary: ["Chest","Shoulders"], unit: "kg_assist", defaults: { sets: 2, reps: 10, weight: -20 } },
  { id: "abductor_machine",  name: "Abductor Machine",       category: "Machines", primary: "Glutes",                                   unit: "kg",        defaults: { sets: 2, reps: 12, weight: 40 } },
  { id: "adductor_machine",  name: "Adductor Machine",       category: "Machines", primary: "Legs",                                     unit: "kg",        defaults: { sets: 2, reps: 12, weight: 40 } },
  { id: "leg_press",         name: "Leg Press",              category: "Machines", primary: "Legs",      secondary: ["Glutes"],         unit: "kg",        defaults: { sets: 3, reps: 10, weight: 80 } },
  { id: "leg_extension",     name: "Leg Extension Machine",          category: "Machines", primary: "Legs",                                     unit: "kg",        defaults: { sets: 2, reps: 12, weight: 30 } },
  { id: "leg_curl",          name: "Hamstring Curl Machine",        category: "Machines", primary: "Legs",                                     unit: "kg",        defaults: { sets: 2, reps: 12, weight: 30 } },
  { id: "calf_raise_machine",name: "Calf Raise (machine)",   category: "Machines", primary: "Legs",                                     unit: "kg",        defaults: { sets: 3, reps: 15, weight: 60 } },
  { id: "lat_pulldown",      name: "Lat Pulldown",           category: "Machines", primary: "Back",      secondary: ["Biceps"],         unit: "kg",        defaults: { sets: 3, reps: 10, weight: 40 } },
  { id: "lat_pulldown_o",    name: "Lat Pulldown (Overhand Wide)", category: "Machines", primary: "Back", secondary: ["Lats","Rear delts"], unit: "kg",   defaults: { sets: 3, reps: 10, weight: 40 } },
  { id: "seated_row",        name: "Seated Cable Row",       category: "Machines", primary: "Back",      secondary: ["Biceps"],         unit: "kg",        defaults: { sets: 3, reps: 10, weight: 40 } },
  { id: "cable_fly",         name: "Cable Fly",              category: "Machines", primary: "Chest",     secondary: ["Shoulders"],      unit: "kg",        defaults: { sets: 2, reps: 12, weight: 12 } },
  { id: "cable_tricep",      name: "Tricep Pushdown Machine",  category: "Machines", primary: "Triceps",                                  unit: "kg",        defaults: { sets: 2, reps: 12, weight: 20 } },
  { id: "cable_bicep",       name: "Cable Bicep Curl",       category: "Machines", primary: "Biceps",                                   unit: "kg",        defaults: { sets: 2, reps: 12, weight: 20 } },
  { id: "cable_woodchop",    name: "Cable Wood Chop",        category: "Machines", primary: "Core",                                     unit: "kg",        defaults: { sets: 2, reps: 12, weight: 15 } },
  { id: "chest_press_machine", name: "Chest Press (machine)",category: "Machines", primary: "Chest",     secondary: ["Triceps","Shoulders"], unit: "kg",   defaults: { sets: 3, reps: 10, weight: 30 } },
  { id: "shoulder_press_machine", name: "Shoulder Press (machine)", category: "Machines", primary: "Shoulders", secondary: ["Triceps"], unit: "kg",        defaults: { sets: 3, reps: 10, weight: 25 } },
  { id: "smith_squat",       name: "Smith Squat",            category: "Machines", primary: "Legs",      secondary: ["Glutes"],         unit: "kg",        defaults: { sets: 3, reps: 8,  weight: 40 } },
  { id: "hip_thrust_machine",name: "Hip Thrust (machine)",   category: "Machines", primary: "Glutes",    secondary: ["Legs"],           unit: "kg",        defaults: { sets: 3, reps: 10, weight: 60 } },
  { id: "back_extension",    name: "Back Extension",         category: "Machines", primary: "Back",      secondary: ["Glutes"],         unit: "kg",        defaults: { sets: 2, reps: 12, weight: 0 } },
  { id: "ab_crunch_machine", name: "Ab Crunch (machine)",    category: "Machines", primary: "Core",                                     unit: "kg",        defaults: { sets: 2, reps: 15, weight: 25 } },

  // ───────── Technogym selection (most popular machines) ─────────
  { id: "tg_chest_press",       name: "Chest Press (Technogym)",          category: "Machines", primary: "Chest",     secondary: ["Triceps","Shoulders"], unit: "kg", defaults: { sets: 3, reps: 10, weight: 30 } },
  { id: "tg_pectoral",          name: "Pectoral Machine (Technogym)",     category: "Machines", primary: "Chest",                                       unit: "kg", defaults: { sets: 3, reps: 12, weight: 25 } },
  { id: "tg_shoulder_press",    name: "Shoulder Press (Technogym)",       category: "Machines", primary: "Shoulders", secondary: ["Triceps"],           unit: "kg", defaults: { sets: 3, reps: 10, weight: 20 } },
  { id: "tg_lateral_raise",     name: "Lateral Raise (Technogym)",        category: "Machines", primary: "Shoulders",                                   unit: "kg", defaults: { sets: 3, reps: 12, weight: 15 } },
  { id: "tg_lat_machine",       name: "Lat Machine (Technogym)",          category: "Machines", primary: "Back",      secondary: ["Biceps"],            unit: "kg", defaults: { sets: 3, reps: 10, weight: 40 } },
  { id: "tg_vertical_traction", name: "Vertical Traction (Technogym)",    category: "Machines", primary: "Back",      secondary: ["Biceps"],            unit: "kg", defaults: { sets: 3, reps: 10, weight: 40 } },
  { id: "tg_low_row",           name: "Low Row (Technogym)",              category: "Machines", primary: "Back",      secondary: ["Biceps"],            unit: "kg", defaults: { sets: 3, reps: 10, weight: 40 } },
  { id: "tg_pulldown",          name: "Pulldown (Technogym)",             category: "Machines", primary: "Back",      secondary: ["Biceps"],            unit: "kg", defaults: { sets: 3, reps: 10, weight: 40 } },
  { id: "tg_rear_delt",         name: "Rear Delt (Technogym)",            category: "Machines", primary: "Shoulders", secondary: ["Back"],              unit: "kg", defaults: { sets: 3, reps: 12, weight: 20 } },
  { id: "tg_arm_curl",          name: "Arm Curl (Technogym)",             category: "Machines", primary: "Biceps",                                      unit: "kg", defaults: { sets: 3, reps: 12, weight: 20 } },
  { id: "tg_arm_extension",     name: "Arm Extension (Technogym)",        category: "Machines", primary: "Triceps",                                     unit: "kg", defaults: { sets: 3, reps: 12, weight: 25 } },
  { id: "tg_leg_press",         name: "Leg Press (Technogym)",            category: "Machines", primary: "Legs",      secondary: ["Glutes"],            unit: "kg", defaults: { sets: 3, reps: 10, weight: 100 } },
  { id: "tg_leg_extension",     name: "Leg Extension (Technogym)",        category: "Machines", primary: "Legs",                                        unit: "kg", defaults: { sets: 3, reps: 12, weight: 35 } },
  { id: "tg_leg_curl",          name: "Leg Curl (Technogym)",             category: "Machines", primary: "Legs",                                        unit: "kg", defaults: { sets: 3, reps: 12, weight: 35 } },
  { id: "tg_glute",             name: "Glute Machine (Technogym)",        category: "Machines", primary: "Glutes",    secondary: ["Legs"],              unit: "kg", defaults: { sets: 3, reps: 12, weight: 40 } },
  { id: "tg_abductor",          name: "Abductor (Technogym)",             category: "Machines", primary: "Glutes",                                      unit: "kg", defaults: { sets: 3, reps: 15, weight: 45 } },
  { id: "tg_adductor",          name: "Adductor (Technogym)",             category: "Machines", primary: "Legs",                                        unit: "kg", defaults: { sets: 3, reps: 15, weight: 45 } },
  { id: "tg_total_abdominal",   name: "Total Abdominal (Technogym)",      category: "Machines", primary: "Core",                                        unit: "kg", defaults: { sets: 3, reps: 15, weight: 30 } },
  { id: "tg_lower_back",        name: "Lower Back (Technogym)",           category: "Machines", primary: "Back",                                        unit: "kg", defaults: { sets: 3, reps: 12, weight: 35 } },
  { id: "tg_calf",              name: "Calf Machine (Technogym)",         category: "Machines", primary: "Legs",                                        unit: "kg", defaults: { sets: 3, reps: 15, weight: 60 } },
  { id: "tg_kinesis_chest",     name: "Kinesis Chest (Technogym)",        category: "Machines", primary: "Chest",     secondary: ["Shoulders"],         unit: "kg", defaults: { sets: 3, reps: 12, weight: 15 } },
  { id: "tg_kinesis_pull",      name: "Kinesis Pull (Technogym)",         category: "Machines", primary: "Back",      secondary: ["Biceps"],            unit: "kg", defaults: { sets: 3, reps: 12, weight: 15 } },
  { id: "tg_smith_squat",       name: "Multipower Squat (Technogym)",     category: "Machines", primary: "Legs",      secondary: ["Glutes"],            unit: "kg", defaults: { sets: 3, reps: 8,  weight: 50 } },
  // ───────── Technogym cardio ─────────
  { id: "tg_skillmill",         name: "Skillmill (Technogym)",            category: "Cardio",   primary: "Cardio",    secondary: ["Legs"],              unit: "min", defaults: { durationMin: 15 }, cardioFields: ["distance"] },
  { id: "tg_skillrun",          name: "Skillrun (Technogym)",             category: "Cardio",   primary: "Cardio",                                      unit: "min", defaults: { durationMin: 20, inclinePct: 1, speedKmh: 9.0 }, cardioFields: ["incline","speed"] },
  { id: "tg_skillrow",          name: "Skillrow (Technogym)",             category: "Cardio",   primary: "Cardio",    secondary: ["Back","Legs"],       unit: "min", defaults: { durationMin: 15 }, cardioFields: ["distance"] },
  { id: "tg_skillbike",         name: "Skillbike (Technogym)",            category: "Cardio",   primary: "Cardio",    secondary: ["Legs"],              unit: "min", defaults: { durationMin: 20 }, cardioFields: ["distance"] },
  { id: "tg_synchro",           name: "Synchro Forma (Technogym)",        category: "Cardio",   primary: "Cardio",                                      unit: "min", defaults: { durationMin: 20 } },
  { id: "tg_recline_bike",      name: "Recline Bike (Technogym)",         category: "Cardio",   primary: "Cardio",    secondary: ["Legs"],              unit: "min", defaults: { durationMin: 20 }, cardioFields: ["distance"] },
  { id: "tg_excite_treadmill",  name: "Excite Treadmill (Technogym)",     category: "Cardio",   primary: "Cardio",                                      unit: "min", defaults: { durationMin: 20, inclinePct: 1, speedKmh: 9.0 }, cardioFields: ["incline","speed"] },

  // ───────── Free weights ─────────
  { id: "db_standing_row",           name: "DB Standing Row",              category: "Free weights", primary: "Back",      secondary: ["Biceps","Rear delts"], unit: "kg", defaults: { sets: 3, reps: 10, weight: 12 } },
  { id: "db_standing_shoulder_press",name: "DB Standing Shoulder Press",  category: "Free weights", primary: "Shoulders", secondary: ["Triceps","Core"],       unit: "kg", defaults: { sets: 3, reps: 10, weight: 10 } },
  { id: "db_lunge_curl",             name: "Backward Lunge with DB Curl", category: "Free weights", primary: "Legs",      secondary: ["Biceps","Glutes"],      unit: "kg", defaults: { sets: 3, reps: 10, weight: 8 } },
  { id: "db_squat_press",            name: "DB Squat to Shoulder Press",  category: "Free weights", primary: "Legs",      secondary: ["Shoulders","Glutes"],   unit: "kg", defaults: { sets: 3, reps: 10, weight: 8 } },
  { id: "db_oh_tricep_ext",          name: "DB Overhead Tricep Extension",category: "Free weights", primary: "Triceps",                                        unit: "kg", defaults: { sets: 2, reps: 12, weight: 10 } },
  { id: "db_lateral_raise",  name: "DB Lateral Raises",       category: "Free weights", primary: "Shoulders",                            unit: "kg",        defaults: { sets: 2, reps: 12, weight: 6 } },
  { id: "db_bent_lateral",   name: "Bent-Over DB Lateral Raise", category: "Free weights", primary: "Rear delts", secondary: ["Back"],   unit: "kg",        defaults: { sets: 3, reps: 12, weight: 6 } },
  { id: "db_front_raise",    name: "DB Front Raise",         category: "Free weights", primary: "Shoulders",                            unit: "kg",        defaults: { sets: 2, reps: 12, weight: 6 } },
  { id: "db_curl",           name: "DB Bicep Curls",                category: "Free weights", primary: "Biceps",                               unit: "kg",        defaults: { sets: 2, reps: 10, weight: 10 } },
  { id: "db_hammer_curl",    name: "DB Hammer Curl",         category: "Free weights", primary: "Biceps",                               unit: "kg",        defaults: { sets: 2, reps: 10, weight: 10 } },
  { id: "arnold_press",      name: "Arnold Press",           category: "Free weights", primary: "Shoulders", secondary: ["Triceps"],    unit: "kg",        defaults: { sets: 2, reps: 10, weight: 8 } },
  { id: "db_shoulder_press", name: "DB Shoulder Press",      category: "Free weights", primary: "Shoulders", secondary: ["Triceps"],    unit: "kg",        defaults: { sets: 3, reps: 10, weight: 12 } },
  { id: "db_bench",          name: "DB Bench Press",         category: "Free weights", primary: "Chest",     secondary: ["Triceps","Shoulders"], unit: "kg", defaults: { sets: 3, reps: 10, weight: 18 } },
  { id: "db_incline_bench",  name: "DB Incline Bench",       category: "Free weights", primary: "Chest",     secondary: ["Shoulders"],  unit: "kg",        defaults: { sets: 3, reps: 10, weight: 16 } },
  { id: "db_fly",            name: "DB Fly",                 category: "Free weights", primary: "Chest",                                unit: "kg",        defaults: { sets: 2, reps: 12, weight: 10 } },
  { id: "db_row",            name: "DB Row (single arm)",    category: "Free weights", primary: "Back",      secondary: ["Biceps"],     unit: "kg",        defaults: { sets: 3, reps: 10, weight: 16 } },
  { id: "db_rdl",            name: "DB Romanian Deadlift",   category: "Free weights", primary: "Glutes",    secondary: ["Legs","Back"], unit: "kg",       defaults: { sets: 3, reps: 10, weight: 16 } },
  { id: "db_sldl",           name: "DB Stiff-Legged Deadlift", category: "Free weights", primary: "Hamstrings", secondary: ["Glutes","Back"], unit: "kg",   defaults: { sets: 3, reps: 10, weight: 16 } },
  { id: "db_goblet_squat",   name: "DB Goblet Squat",        category: "Free weights", primary: "Legs",      secondary: ["Glutes"],     unit: "kg",        defaults: { sets: 3, reps: 10, weight: 16 } },
  { id: "db_lunge",          name: "DB Walking Lunge",       category: "Free weights", primary: "Legs",      secondary: ["Glutes"],     unit: "kg",        defaults: { sets: 2, reps: 12, weight: 10 } },
  { id: "db_side_lunge",     name: "DB Side Lunge",          category: "Free weights", primary: "Legs",      secondary: ["Glutes","Adductors"], unit: "kg", defaults: { sets: 3, reps: 10, weight: 10 } },
  { id: "db_step_up",        name: "DB Step-up",             category: "Free weights", primary: "Legs",      secondary: ["Glutes"],     unit: "kg",        defaults: { sets: 2, reps: 10, weight: 10 } },
  { id: "db_skullcrusher",   name: "DB Skullcrusher",        category: "Free weights", primary: "Triceps",                              unit: "kg",        defaults: { sets: 2, reps: 12, weight: 8 } },
  { id: "db_kickback",       name: "DB Tricep Kickback",     category: "Free weights", primary: "Triceps",                              unit: "kg",        defaults: { sets: 2, reps: 12, weight: 6 } },
  { id: "db_shrug",          name: "DB Shrug",               category: "Free weights", primary: "Back",                                 unit: "kg",        defaults: { sets: 2, reps: 12, weight: 14 } },
  { id: "kb_swing",          name: "Kettlebell Swing",       category: "Free weights", primary: "Glutes",    secondary: ["Back","Core"], unit: "kg",       defaults: { sets: 3, reps: 15, weight: 16 } },
  { id: "bb_squat",          name: "Barbell Back Squat",     category: "Free weights", primary: "Legs",      secondary: ["Glutes"],     unit: "kg",        defaults: { sets: 3, reps: 8,  weight: 50 } },
  { id: "bb_deadlift",       name: "Barbell Deadlift",       category: "Free weights", primary: "Back",      secondary: ["Glutes","Legs"], unit: "kg",     defaults: { sets: 3, reps: 5,  weight: 70 } },
  { id: "bb_bench",          name: "Barbell Bench Press",    category: "Free weights", primary: "Chest",     secondary: ["Triceps","Shoulders"], unit: "kg", defaults: { sets: 3, reps: 8,  weight: 50 } },
  { id: "bb_overhead_press", name: "Barbell Overhead Press", category: "Free weights", primary: "Shoulders", secondary: ["Triceps"],    unit: "kg",        defaults: { sets: 3, reps: 8,  weight: 30 } },
  { id: "bb_row",            name: "Barbell Row",            category: "Free weights", primary: "Back",      secondary: ["Biceps"],     unit: "kg",        defaults: { sets: 3, reps: 8,  weight: 40 } },
  { id: "db_back_lunge",     name: "DB Backward Lunges",     category: "Free weights", primary: "Legs",      secondary: ["Glutes"],     unit: "kg",        defaults: { sets: 3, reps: 12, weight: 10 } },
  { id: "db_sldl_upright_row", name: "DB Stiff-Leg DL + Upright Row", category: "Free weights", primary: "Hamstrings", secondary: ["Shoulders","Back"], unit: "kg", defaults: { sets: 3, reps: 12, weight: 12 } },
  { id: "db_russian_twist",  name: "DB Russian Twists",      category: "Free weights", primary: "Core",      secondary: ["Obliques"],   unit: "kg",        defaults: { sets: 3, reps: 12, weight: 5 } },

  // ───────── Bodyweight ─────────
  { id: "bw_pullup",         name: "Pull-up",                category: "Bodyweight",   primary: "Back",      secondary: ["Biceps"],     unit: "reps",      defaults: { sets: 3, reps: 6 } },
  { id: "bw_dip",            name: "Bodyweight Dip",         category: "Bodyweight",   primary: "Triceps",   secondary: ["Chest","Shoulders"], unit: "reps", defaults: { sets: 2, reps: 8 } },
  { id: "bw_pushup",         name: "Push-up",                category: "Bodyweight",   primary: "Chest",     secondary: ["Triceps","Shoulders"], unit: "reps", defaults: { sets: 3, reps: 12 } },
  { id: "bw_incline_pushup", name: "Incline Push-up",        category: "Bodyweight",   primary: "Chest",     secondary: ["Triceps"],    unit: "reps",      defaults: { sets: 3, reps: 12 } },
  { id: "bw_squat",          name: "Bodyweight Squat",       category: "Bodyweight",   primary: "Legs",      secondary: ["Glutes"],     unit: "reps",      defaults: { sets: 3, reps: 15 } },
  { id: "bw_lunge",          name: "Bodyweight Lunge",       category: "Bodyweight",   primary: "Legs",      secondary: ["Glutes"],     unit: "reps",      defaults: { sets: 2, reps: 12 } },
  { id: "bw_glute_bridge",   name: "Glute Bridge",           category: "Bodyweight",   primary: "Glutes",                               unit: "reps",      defaults: { sets: 2, reps: 15 } },
  { id: "bw_plank",          name: "Plank",                  category: "Bodyweight",   primary: "Core",                                 unit: "min",       defaults: { sets: 3, durationMin: 1 } },
  { id: "bw_side_plank",     name: "Side Plank",             category: "Bodyweight",   primary: "Core",                                 unit: "min",       defaults: { sets: 2, durationMin: 0.5 } },
  { id: "bw_crunch",         name: "Crunch",                 category: "Bodyweight",   primary: "Core",                                 unit: "reps",      defaults: { sets: 2, reps: 15 } },
  { id: "bw_leg_raise",      name: "Hanging Leg Raise",      category: "Bodyweight",   primary: "Core",                                 unit: "reps",      defaults: { sets: 2, reps: 10 } },
  { id: "bw_mountain_climb", name: "Mountain Climbers",      category: "Bodyweight",   primary: "Core",      secondary: ["Cardio"],     unit: "reps",      defaults: { sets: 2, reps: 30 } },
  { id: "bw_burpee",         name: "Burpee",                 category: "Bodyweight",   primary: "Full body", secondary: ["Cardio"],     unit: "reps",      defaults: { sets: 3, reps: 10 } },
  { id: "bw_bench_situp",    name: "Bench Sit-Ups",          category: "Bodyweight",   primary: "Core",                                 unit: "reps",      defaults: { sets: 3, reps: 12 } },
  { id: "bw_bench_leg_raise",name: "Bench Leg Raises",       category: "Bodyweight",   primary: "Core",                                 unit: "reps",      defaults: { sets: 3, reps: 12 } },
  { id: "bw_jump_squat",     name: "Jump Squat",             category: "Bodyweight",   primary: "Legs",      secondary: ["Glutes","Cardio"], unit: "reps", defaults: { sets: 3, reps: 30 } },
  { id: "bw_wide_squat_taps",name: "Wide Squat Narrow Taps", category: "Bodyweight",   primary: "Legs",      secondary: ["Cardio"],     unit: "reps",      defaults: { sets: 3, reps: 30 } },
  { id: "bw_star_jump",      name: "Star Jumps",             category: "Bodyweight",   primary: "Full body", secondary: ["Cardio"],     unit: "reps",      defaults: { sets: 3, reps: 30 } },

  // ───────── Cardio ─────────
  { id: "treadmill_walk",    name: "Treadmill Walk",         category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 15, inclinePct: 6,  speedKmh: 5.5 }, cardioFields: ["incline","speed"] },
  { id: "treadmill_run",     name: "Treadmill Run",          category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 20, inclinePct: 1,  speedKmh: 9.0 }, cardioFields: ["incline","speed"] },
  { id: "outdoor_walk",      name: "Outdoor Walk",           category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 30 }, cardioFields: ["distance"] },
  { id: "outdoor_run",       name: "Outdoor Run",            category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 30 }, cardioFields: ["distance"] },
  { id: "stationary_bike",   name: "Stationary Bike",        category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 20 }, cardioFields: ["resistance","distance"] },
  { id: "spin_bike",         name: "Spin Class",             category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 45 } },
  { id: "rower",             name: "Rower",                  category: "Cardio",       primary: "Cardio",     secondary: ["Back","Legs"],unit: "min",       defaults: { durationMin: 15 }, cardioFields: ["distance"] },
  { id: "stair_master",      name: "Stair Master",           category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 15 } },
  { id: "elliptical",        name: "Elliptical",             category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 20 } },
  { id: "jump_rope",         name: "Jump Rope",              category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 10 } },
  { id: "swim",              name: "Swim",                   category: "Cardio",       primary: "Cardio",     secondary: ["Full body"], unit: "min",       defaults: { durationMin: 30 }, cardioFields: ["distance"] },
  { id: "cycle_outdoor",     name: "Cycle (outdoor)",        category: "Cardio",       primary: "Cardio",                               unit: "min",       defaults: { durationMin: 45 }, cardioFields: ["distance"] },

  // ───────── Stretch / Mobility ─────────
  { id: "stretch_full",      name: "Full-body Stretch",      category: "Stretch",      primary: "Full body",                            unit: "min",       defaults: { durationMin: 10 } },
  { id: "stretch_hip",       name: "Hip Mobility",           category: "Stretch",      primary: "Legs",                                 unit: "min",       defaults: { durationMin: 8 } },
  { id: "stretch_shoulder",  name: "Shoulder Mobility",      category: "Stretch",      primary: "Shoulders",                            unit: "min",       defaults: { durationMin: 6 } },
  { id: "foam_rolling",      name: "Foam Rolling",           category: "Stretch",      primary: "Full body",                            unit: "min",       defaults: { durationMin: 8 } },
  { id: "yoga",              name: "Yoga",                   category: "Stretch",      primary: "Full body",                            unit: "min",       defaults: { durationMin: 30 } },

  // ───────── Night wind-down (bed-friendly, gentle, no load) ─────────
  { id: "st_diaphragm_breath", name: "Diaphragmatic Breathing", category: "Stretch", primary: "Core",   unit: "min", defaults: { sets: 1, durationMin: 2 } },
  { id: "st_knee_to_chest",    name: "Knee-to-Chest",           category: "Stretch", primary: "Back",   unit: "min", defaults: { sets: 1, durationMin: 1 } },
  { id: "st_supine_twist",     name: "Supine Spinal Twist",     category: "Stretch", primary: "Back",   unit: "min", defaults: { sets: 1, durationMin: 1 } },
  { id: "st_figure4_supine",   name: "Reclined Figure-4",       category: "Stretch", primary: "Glutes", unit: "min", defaults: { sets: 1, durationMin: 1 } },
  { id: "st_supine_hamstring", name: "Supine Hamstring Stretch",category: "Stretch", primary: "Legs",   unit: "min", defaults: { sets: 1, durationMin: 1 } },
  { id: "st_happy_baby",       name: "Happy Baby Pose",         category: "Stretch", primary: "Back",   unit: "min", defaults: { sets: 1, durationMin: 1 } },
  { id: "st_child_pose",       name: "Child's Pose",            category: "Stretch", primary: "Back",   unit: "min", defaults: { sets: 1, durationMin: 1 } },
  { id: "st_cat_cow",          name: "Cat-Cow (gentle)",        category: "Stretch", primary: "Back",   unit: "min", defaults: { sets: 1, durationMin: 1 } },
  { id: "st_neck_release",     name: "Neck Release",            category: "Stretch", primary: "Neck",   unit: "min", defaults: { sets: 1, durationMin: 1 } },
  { id: "st_legs_up_wall",     name: "Legs Up the Wall",        category: "Stretch", primary: "Legs",   unit: "min", defaults: { sets: 1, durationMin: 3 } },
  { id: "st_box_breath",       name: "Box Breathing",           category: "Stretch", primary: "Core",   unit: "min", defaults: { sets: 1, durationMin: 2 } },
];

export const EXERCISE_BY_ID = Object.fromEntries(EXERCISE_LIBRARY.map(e => [e.id, e]));

// ───────── Helpers ─────────

export const localDateStr = (offsetDays = 0) => {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
};

export const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

// ISO week-key (YYYY-Www) used for streak bucketing — Monday is the start
// of the training week, matching the user's PT schedule.
export function isoWeekKey(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((dt - yearStart) / 86400000) + 1) / 7);
  return `${dt.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

// Epley 1RM: w * (1 + reps/30). Returns 0 for bodyweight / assisted.
export function est1RM(weight, reps) {
  if (typeof weight !== "number" || weight <= 0 || !reps) return 0;
  return Math.round(weight * (1 + reps / 30));
}

// DEPRECATED (v2 audit, 2026-05-24): operates on the nested
// `circuits[].exercises[].sets[]` shape declared in newSession() below,
// but the persisted shape in localStorage.sirat-workouts is the flat
// `exercises[].sets[]` form. The live volume calc is Riyadah.js
// workoutVolume() (line ~3881). Kept until a workout-schema cleanup pass.
// Working volume: sum of weight * reps across loaded sets only.
// Bodyweight & assisted contribute to "BW reps" separately, never to volume.
export function sessionVolume(session) {
  if (!session?.circuits) return 0;
  let v = 0;
  for (const c of session.circuits) {
    for (const ex of c.exercises) {
      const def = EXERCISE_BY_ID[ex.exerciseId];
      if (!def || (def.unit !== "kg")) continue;
      for (const s of ex.sets) {
        if (typeof s.weight === "number" && s.weight > 0 && s.reps > 0) {
          v += s.weight * s.reps;
        }
      }
    }
  }
  return v;
}

// DEPRECATED (v2 audit, 2026-05-24): see sessionVolume note above.
export function sessionBwReps(session) {
  if (!session?.circuits) return 0;
  let r = 0;
  for (const c of session.circuits) {
    for (const ex of c.exercises) {
      const def = EXERCISE_BY_ID[ex.exerciseId];
      if (!def || (def.unit !== "reps" && def.unit !== "kg_assist")) continue;
      for (const s of ex.sets) {
        if (s.reps > 0) r += s.reps;
      }
    }
  }
  return r;
}

// DEPRECATED (v2 audit, 2026-05-24): operates on the nested circuits shape; unused.
export function sessionTotalSets(session) {
  if (!session?.circuits) return 0;
  let n = 0;
  for (const c of session.circuits) for (const ex of c.exercises) n += (ex.sets?.length || 0);
  return n;
}

// DEPRECATED (v2 audit, 2026-05-24): nested circuits shape; use
// extractMuscleGroups() on the flat exercises[] shape instead.
export function muscleGroupsHit(session) {
  const hit = new Set();
  if (!session?.circuits) return hit;
  for (const c of session.circuits) {
    for (const ex of c.exercises) {
      const def = EXERCISE_BY_ID[ex.exerciseId];
      if (!def) continue;
      hit.add(def.primary);
      for (const s of (def.secondary || [])) hit.add(s);
    }
  }
  if (session.cardio?.length) hit.add("Cardio");
  return hit;
}

// DEPRECATED (v2 audit, 2026-05-24): no external callers.
// "60kg × 10" formatter; handles assist (negative), bodyweight (no weight),
// duration-based (plank, cardio).
export function formatSet(set, def) {
  if (!def) return "";
  if (def.unit === "min") {
    const m = set.durationMin ?? def.defaults?.durationMin ?? 0;
    return `${m}m`;
  }
  if (def.unit === "reps") {
    return `× ${set.reps ?? "—"}`;
  }
  if (def.unit === "kg_assist") {
    const w = Math.abs(set.weight ?? 0);
    return `−${w}kg × ${set.reps ?? "—"}`;
  }
  return `${set.weight ?? 0}kg × ${set.reps ?? "—"}`;
}

// DEPRECATED (v2 audit, 2026-05-24): defines the nested-circuits shape
// that is NOT what gets persisted. Actual persistence is in Riyadah.js
// confirmFinish() and uses a flat exercises[].sets[] form. Kept until a
// workout-schema cleanup pass — see HAYAH.md §Workout schema split.
// Build a fresh empty session for `dateStr`.
export function newSession(dateStr) {
  return {
    sessionId: genId(),
    date: dateStr,
    startedAt: new Date().toISOString(),
    durationMin: null,
    activityType: null,
    withTrainer: false,
    trainerName: "",
    healthWorkoutId: null,
    caloriesBurned: 0,
    avgHr: 0, maxHr: 0,
    hrZoneSeconds: null,
    circuits: [],
    cardio: [],
    notes: "",
  };
}

export const DEFAULT_GOALS = { sessionsPerWeek: 3, weeklyVolumeKg: 12000, cardioMin: 60 };

// Scan the log and roll up a streak in weeks (consecutive ISO weeks meeting the
// sessionsPerWeek goal). Returns { current, longest, thisWeekHit }.
export function computeStreak(log, goalSessionsPerWeek) {
  const dates = Object.keys(log || {}).sort();
  if (!dates.length) return { current: 0, longest: 0, thisWeekHit: 0 };
  const byWeek = {};
  for (const d of dates) {
    const wk = isoWeekKey(d);
    byWeek[wk] = (byWeek[wk] || 0) + 1;
  }
  const sortedWeeks = Object.keys(byWeek).sort();
  const thisWeek = isoWeekKey(localDateStr());
  const thisWeekHit = byWeek[thisWeek] || 0;

  // Walk backward from the most recent week-meeting-goal that is either this
  // week or the immediately prior week (allow one rest week without breaking).
  let current = 0;
  let cursor = thisWeek;
  // Helper: given a week key, return previous week key.
  const prevWeek = (wk) => {
    const [yStr, wStr] = wk.split("-W");
    let y = +yStr, w = +wStr - 1;
    if (w < 1) { y -= 1; w = 52; }
    return `${y}-W${String(w).padStart(2,"0")}`;
  };
  // If this week not yet hit, start from previous week.
  if ((byWeek[cursor] || 0) < goalSessionsPerWeek) cursor = prevWeek(cursor);
  while ((byWeek[cursor] || 0) >= goalSessionsPerWeek) {
    current += 1;
    cursor = prevWeek(cursor);
  }

  // Longest streak in history.
  let longest = 0, run = 0, prev = null;
  for (const wk of sortedWeeks) {
    if ((byWeek[wk] || 0) < goalSessionsPerWeek) { run = 0; prev = null; continue; }
    if (prev && prevWeek(wk) === prev) run += 1; else run = 1;
    if (run > longest) longest = run;
    prev = wk;
  }
  if (current > longest) longest = current;

  return { current, longest, thisWeekHit };
}

// DEPRECATED (v2 audit, 2026-05-24): no external callers.
// PR check: returns the new top weight and 1RM for the exerciseId after
// integrating `set`. Caller decides whether it beats prior PR.
export function setPR(set) {
  return { topWeight: set.weight || 0, est1RM: est1RM(set.weight, set.reps), reps: set.reps };
}

// DEPRECATED (v2 audit, 2026-05-24): operates on the nested circuits shape;
// the v2 AI workout generator uses loadLastExerciseData() (flat shape) instead.
// Find the best set the user did the last time they performed `exerciseId`,
// skipping today. "Best" means highest weight; ties broken by reps. Returns
// { date, weight, reps, durationMin, daysAgo } or null if never done.
export function lastPerformance(log, exerciseId, todayStr) {
  if (!log) return null;
  const dates = Object.keys(log).filter(d => d < todayStr).sort().reverse();
  for (const d of dates) {
    const session = log[d];
    if (!session?.circuits) continue;
    let best = null;
    for (const c of session.circuits) {
      for (const ex of (c.exercises || [])) {
        if (ex.exerciseId !== exerciseId) continue;
        for (const s of ex.sets || []) {
          if (!best) { best = s; continue; }
          const w = (s.weight ?? -Infinity), bw = (best.weight ?? -Infinity);
          if (w > bw || (w === bw && (s.reps || 0) > (best.reps || 0))) best = s;
        }
      }
    }
    // Cardio entries on the row.
    for (const c of (session.cardio || [])) {
      if (c.exerciseId !== exerciseId) continue;
      if (!best || (c.durationMin || 0) > (best.durationMin || 0)) best = c;
    }
    if (best) {
      const today = new Date(todayStr);
      const past = new Date(d);
      const daysAgo = Math.round((today - past) / 86400000);
      return {
        date: d, daysAgo,
        weight: best.weight ?? null,
        reps: best.reps ?? null,
        durationMin: best.durationMin ?? null,
      };
    }
  }
  return null;
}

export function formatDaysAgo(n) {
  if (n <= 0) return "today";
  if (n === 1) return "yesterday";
  if (n < 7) return `${n} days ago`;
  if (n < 14) return "last week";
  if (n < 30) return `${Math.round(n / 7)} wks ago`;
  return `${Math.round(n / 30)} mo ago`;
}

// ─── Templates (gold-standard logger: Hub → Pick → Active flow) ─────────────
// The user trains 3x/week with a PT in two specific circuits — pre-load those
// as first-class templates plus a Full Body and Quick Start.

export const RIYADAH_TEMPLATES = [
  {
    id: "quick-start", name: "Quick Start", emoji: "▶", tag: "push",
    desc: "Empty template — build as you go",
    circuits: 2,
    exercises_list: [],
    cardio: null,
  },
  {
    // Sourced from PT WhatsApp: Block A pull/legs · Block B chest/clean/lunges
    // · Block C cables. Three rounds × 10 reps each.
    id: "circuit-a", name: "Circuit A", emoji: "💪", tag: "pull",
    desc: "Pull · Push · Legs · Cables · 3 × 10",
    circuits: 3,
    blockLabels: { A: "Pull · Arms · Legs", B: "Chest · Power · Legs", C: "Cables · Triceps · Pull" },
    exercises_list: [
      { block: "A", id: "lat_pulldown",          name: "Lat Pulldown",            type: "pull", muscles: "Back, Biceps",      defaultWeight: 40, defaultReps: 10 },
      { block: "A", id: "db_curl",               name: "DB Bicep Curls",           type: "pull", muscles: "Biceps",            defaultWeight: 10, defaultReps: 10 },
      { block: "A", id: "db_oh_tricep",          name: "DB Overhead Tricep Ext.", type: "push", muscles: "Triceps",           defaultWeight: 10, defaultReps: 10 },
      { block: "A", id: "bw_squat",              name: "BW Squats",               type: "legs", muscles: "Quads, Glutes",     defaultWeight: 0,  defaultReps: 10 },
      { block: "B", id: "chest_press_machine",   name: "Seated Chest Press",      type: "push", muscles: "Chest, Triceps",    defaultWeight: 30, defaultReps: 10 },
      { block: "B", id: "db_clean_press",        name: "DB Clean & Press",        type: "push", muscles: "Shoulders, Back",   defaultWeight: 12, defaultReps: 10 },
      { block: "B", id: "bw_back_lunge",         name: "BW Backward Lunges",      type: "legs", muscles: "Quads, Glutes",     defaultWeight: 0,  defaultReps: 10 },
      { block: "C", id: "cable_tricep",          name: "Tricep Pushdown Machine",   type: "push", muscles: "Triceps",           defaultWeight: 20, defaultReps: 10 },
      { block: "C", id: "cable_rope_pullover",   name: "Cable Rope Pullover",     type: "pull", muscles: "Lats, Back",        defaultWeight: 20, defaultReps: 10 },
      { block: "C", id: "cable_face_pull",       name: "Cable Face Pull",         type: "pull", muscles: "Rear delts, Back",  defaultWeight: 15, defaultReps: 10 },
    ],
    cardio: { name: "Incline Walk", duration: 15, incline: 8, speed: 5.5 },
  },
  {
    id: "circuit-b", name: "Circuit B", emoji: "🏋️", tag: "push",
    desc: "Pull · Chest · Arms · Power · 3 × 10",
    circuits: 3,
    blockLabels: { A: "Pull · Power · Legs · Conditioning", B: "Chest · Arms · Core", C: "Legs · Shoulders · Back · Finisher" },
    exercises_list: [
      { block: "A", id: "lat_pulldown",          name: "Lat Pulldown",            type: "pull", muscles: "Back, Biceps",      defaultWeight: 40, defaultReps: 10 },
      { block: "A", id: "db_clean_press",        name: "DB Clean & Press",        type: "push", muscles: "Shoulders, Back",   defaultWeight: 12, defaultReps: 10 },
      { block: "A", id: "db_curl",               name: "DB Bicep Curls",           type: "pull", muscles: "Biceps",            defaultWeight: 10, defaultReps: 10 },
      { block: "A", id: "bw_back_lunge",         name: "BW Backward Lunges",      type: "legs", muscles: "Quads, Glutes",     defaultWeight: 0,  defaultReps: 10 },
      { block: "B", id: "db_incline_bench",      name: "Incline DB Press",        type: "push", muscles: "Chest, Shoulders",  defaultWeight: 16, defaultReps: 10 },
      { block: "B", id: "db_incline_fly",        name: "Incline DB Fly",          type: "push", muscles: "Chest",             defaultWeight: 10, defaultReps: 10 },
      { block: "B", id: "db_oh_tricep",          name: "DB Overhead Tricep Ext.", type: "push", muscles: "Triceps",           defaultWeight: 10, defaultReps: 10 },
      { block: "B", id: "bw_side_crunch",        name: "Side Crunches",           type: "pull", muscles: "Core, Obliques",    defaultWeight: 0,  defaultReps: 10 },
      { block: "C", id: "leg_press",             name: "Leg Press",               type: "legs", muscles: "Quads, Glutes",     defaultWeight: 80, defaultReps: 10 },
      { block: "C", id: "db_lateral_raise",      name: "DB Lateral Raises",        type: "push", muscles: "Shoulders",         defaultWeight: 6,  defaultReps: 10 },
      { block: "C", id: "db_front_raise",        name: "DB Front Raise",          type: "push", muscles: "Shoulders",         defaultWeight: 6,  defaultReps: 10 },
      { block: "C", id: "db_bent_row",           name: "DB Bent-Over Row",        type: "pull", muscles: "Back, Biceps",      defaultWeight: 14, defaultReps: 10 },
    ],
    cardio: { name: "Incline Walk", duration: 15, incline: 8, speed: 5.5 },
  },
  {
    id: "full-body", name: "Full Body", emoji: "⚡", tag: "push",
    desc: "DB & Bench · Chest · Arms · Legs · 3 × 10",
    circuits: 3,
    blockLabels: { A: "Chest · Power · Arms", B: "Chest · Arms · Shoulders", C: "Legs · Back" },
    exercises_list: [
      { block: "A", id: "db_bench",              name: "Flat DB Chest Press",     type: "push", muscles: "Chest, Triceps",    defaultWeight: 18, defaultReps: 10 },
      { block: "A", id: "db_sumo_clean_press",   name: "DB Sumo Clean & Press",   type: "push", muscles: "Full body",         defaultWeight: 14, defaultReps: 10 },
      { block: "A", id: "db_curl",               name: "DB Bicep Curls",           type: "pull", muscles: "Biceps",            defaultWeight: 10, defaultReps: 10 },
      { block: "B", id: "db_incline_bench",      name: "Incline DB Press",        type: "push", muscles: "Chest, Shoulders",  defaultWeight: 16, defaultReps: 10 },
      { block: "B", id: "db_oh_tricep",          name: "DB Overhead Tricep Ext.", type: "push", muscles: "Triceps",           defaultWeight: 10, defaultReps: 10 },
      { block: "B", id: "db_lateral_raise",      name: "DB Lateral Raises",        type: "push", muscles: "Shoulders",         defaultWeight: 6,  defaultReps: 10 },
      { block: "C", id: "db_sumo_squat",         name: "DB Sumo Squat",           type: "legs", muscles: "Quads, Glutes",     defaultWeight: 16, defaultReps: 10 },
      { block: "C", id: "bw_back_lunge",         name: "BW Backward Lunges",      type: "legs", muscles: "Quads, Glutes",     defaultWeight: 0,  defaultReps: 10 },
      { block: "C", id: "db_bent_row",           name: "DB Bent-Over Row",        type: "pull", muscles: "Back, Biceps",      defaultWeight: 14, defaultReps: 10 },
    ],
    cardio: { name: "Incline Walk", duration: 15, incline: 8, speed: 5.5 },
  },
  {
    id: "full-body-2", name: "Full Body 2", emoji: "🔥", tag: "push",
    desc: "Bench · Pull · Legs · Core · 3 × 10",
    circuits: 3,
    blockLabels: { A: "Chest · Arms", B: "Pull · Shoulders · Back", C: "Legs", D: "Core" },
    exercises_list: [
      { block: "A", id: "bb_bench",              name: "Flat Barbell Bench Press",   type: "push", muscles: "Chest, Triceps",   defaultWeight: 40, defaultReps: 10 },
      { block: "A", id: "db_fly",                name: "DB Chest Fly",               type: "push", muscles: "Chest",            defaultWeight: 10, defaultReps: 10 },
      { block: "A", id: "db_curl",               name: "DB Bicep Curls",              type: "pull", muscles: "Biceps",           defaultWeight: 10, defaultReps: 10 },
      { block: "A", id: "db_oh_tricep",          name: "DB Overhead Tricep Ext.",    type: "push", muscles: "Triceps",          defaultWeight: 10, defaultReps: 10 },
      { block: "B", id: "lat_pulldown_h",        name: "Lat Pulldown (Horiz. Grip)", type: "pull", muscles: "Back, Biceps",     defaultWeight: 40, defaultReps: 10 },
      { block: "B", id: "db_shoulder_press",     name: "Seated DB Shoulder Press",   type: "push", muscles: "Shoulders",        defaultWeight: 12, defaultReps: 10 },
      { block: "B", id: "db_lateral_raise",      name: "DB Lateral Raises",           type: "push", muscles: "Shoulders",        defaultWeight: 6,  defaultReps: 10 },
      { block: "B", id: "seated_row_v",          name: "Seated Row (Vert. Grip)",    type: "pull", muscles: "Back, Biceps",     defaultWeight: 40, defaultReps: 10 },
      { block: "C", id: "leg_extension",         name: "Leg Extension Machine",             type: "legs", muscles: "Quads",            defaultWeight: 30, defaultReps: 10 },
      { block: "C", id: "db_goblet_squat",       name: "Goblet Squat",               type: "legs", muscles: "Quads, Glutes",    defaultWeight: 16, defaultReps: 10 },
      { block: "C", id: "db_lunge",              name: "Walking Lunges",             type: "legs", muscles: "Quads, Glutes",    defaultWeight: 10, defaultReps: 10 },
      { block: "C", id: "db_rdl",                name: "Romanian Deadlifts",         type: "pull", muscles: "Hamstrings, Glutes", defaultWeight: 16, defaultReps: 10 },
      { block: "D", id: "ab_crunch_machine",     name: "Ab Crunch",                  type: "pull", muscles: "Core",             defaultWeight: 25, defaultReps: 10 },
      { block: "D", id: "bw_leg_raise",          name: "Leg Raises",                 type: "pull", muscles: "Core",             defaultWeight: 0,  defaultReps: 10 },
      { block: "D", id: "back_extension",        name: "Hyperextensions",            type: "pull", muscles: "Lower back",       defaultWeight: 0,  defaultReps: 10 },
      { block: "D", id: "bw_side_crunch",        name: "Side Crunches",              type: "pull", muscles: "Core, Obliques",   defaultWeight: 0,  defaultReps: 10 },
    ],
    cardio: null,
  },
  {
    // Sourced from PT WhatsApp: four-block full body with heavy compound at
    // 12 reps + two accessories at 20 reps per block. 10-min incline-12
    // treadmill warm-up; optional row/ski/bike finisher (10–15 cal × 2)
    // is logged separately if used.
    id: "full-body-3", name: "Full Body 3", emoji: "💎", tag: "push",
    desc: "Heavy + accessory · 4 blocks · 3 × (12 heavy / 20 acc.)",
    circuits: 3,
    blockLabels: {
      A: "Legs · Shoulders",
      B: "Pull · Arms",
      C: "Chest · Hamstrings · Triceps",
      D: "Legs · Shoulders",
    },
    exercises_list: [
      // 🔴 Block A
      { block: "A", id: "leg_press",            name: "Leg Press (Heavy)",       type: "legs", muscles: "Quads, Glutes",       defaultWeight: 90, defaultReps: 12 },
      { block: "A", id: "db_goblet_squat",     name: "DB Goblet Squat",         type: "legs", muscles: "Quads, Glutes",       defaultWeight: 16, defaultReps: 20 },
      { block: "A", id: "db_shoulder_press",   name: "DB Shoulder Press",       type: "push", muscles: "Shoulders, Triceps",  defaultWeight: 12, defaultReps: 20 },
      // 🔵 Block B
      { block: "B", id: "seated_row",          name: "Seated Row (Heavy)",      type: "pull", muscles: "Back, Biceps",        defaultWeight: 45, defaultReps: 12 },
      { block: "B", id: "db_bent_row",         name: "DB Bent-Over Row",        type: "pull", muscles: "Back, Biceps",        defaultWeight: 14, defaultReps: 20 },
      { block: "B", id: "db_hammer_curl",      name: "DB Hammer Curl",          type: "pull", muscles: "Biceps, Forearms",    defaultWeight: 10, defaultReps: 20 },
      // 🟢 Block C
      { block: "C", id: "chest_press_machine", name: "Chest Press (Heavy)",     type: "push", muscles: "Chest, Triceps",      defaultWeight: 35, defaultReps: 12 },
      { block: "C", id: "db_sldl_upright_row", name: "Stiff-Leg DL + Upright Row", type: "pull", muscles: "Hamstrings, Shoulders, Back", defaultWeight: 12, defaultReps: 20 },
      { block: "C", id: "db_kickback",         name: "DB Tricep Kickback",      type: "push", muscles: "Triceps",             defaultWeight: 6,  defaultReps: 20 },
      // 🟡 Block D
      { block: "D", id: "leg_extension",       name: "Leg Extension (Heavy)",   type: "legs", muscles: "Quads",               defaultWeight: 35, defaultReps: 12 },
      { block: "D", id: "db_lunge",            name: "DB Walking Lunges",       type: "legs", muscles: "Quads, Glutes",       defaultWeight: 10, defaultReps: 20 },
      { block: "D", id: "db_lateral_raise",    name: "DB Lateral Raises",       type: "push", muscles: "Shoulders",           defaultWeight: 6,  defaultReps: 20 },
    ],
    // Warm-up — 10 min incline walk at incline 12 / speed 5 per PT spec.
    cardio: { name: "Treadmill Walk", duration: 10, incline: 12, speed: 5 },
  },
  {
    // Sourced from PT WhatsApp (Steve): three strength blocks A/B/C, four
    // exercises each, 3 rounds × 10 reps, plus a 30-cal cardio interval
    // between blocks and a 30-cal cardio finisher at the end.
    id: "full-body-3", name: "Full Body 3", emoji: "🔥", tag: "push",
    desc: "Push · Pull · Conditioning · 3 × 10",
    circuits: 3,
    blockLabels: {
      A: "Strength · Push focus",
      B: "Pull · Posterior chain",
      C: "Full-body conditioning",
    },
    exercises_list: [
      { block: "A", id: "db_bench",              name: "DB Chest Press",          type: "push", muscles: "Chest, Triceps",       defaultWeight: 18, defaultReps: 10 },
      { block: "A", id: "db_goblet_squat",       name: "Goblet Squat",            type: "legs", muscles: "Quads, Glutes",        defaultWeight: 16, defaultReps: 10 },
      { block: "A", id: "db_shoulder_press",     name: "Standing Shoulder Press", type: "push", muscles: "Shoulders, Triceps",   defaultWeight: 12, defaultReps: 10 },
      { block: "A", id: "bw_mountain_climb",     name: "Mountain Climbers",       type: "pull", muscles: "Core, Cardio",         defaultWeight: 0,  defaultReps: 30 },
      { block: "B", id: "lat_pulldown",          name: "Lat Pulldown",            type: "pull", muscles: "Back, Biceps",         defaultWeight: 40, defaultReps: 10 },
      { block: "B", id: "db_rdl",                name: "DB Romanian Deadlift",    type: "pull", muscles: "Hamstrings, Glutes",   defaultWeight: 16, defaultReps: 10 },
      { block: "B", id: "seated_row",            name: "Seated Row (Horiz. Grip)",type: "pull", muscles: "Back, Biceps",         defaultWeight: 40, defaultReps: 10 },
      { block: "B", id: "bw_bicycle_crunch",     name: "Bicycle Crunches",        type: "pull", muscles: "Core, Obliques",       defaultWeight: 0,  defaultReps: 20 },
      { block: "C", id: "db_clean_press",        name: "DB Clean & Press",        type: "push", muscles: "Shoulders, Back",      defaultWeight: 12, defaultReps: 10 },
      { block: "C", id: "db_lunge",              name: "Walking Lunges",          type: "legs", muscles: "Quads, Glutes",        defaultWeight: 10, defaultReps: 10 },
      { block: "C", id: "db_bent_row",           name: "DB Bent-Over Row",        type: "pull", muscles: "Back, Biceps",         defaultWeight: 14, defaultReps: 10 },
      { block: "C", id: "bw_plank_taps",         name: "Plank Shoulder Taps",     type: "pull", muscles: "Core, Shoulders",      defaultWeight: 0,  defaultReps: 20 },
    ],
    cardio: { name: "Cardio Finisher", duration: 10, targetCal: 30, mode: "Treadmill / Bike / Row / Ski Erg" },
  },
  {
    // 4-block circuit (A/B/C/D) — strength + conditioning. Each block: 3
    // rounds of 10-15 reps on the strength moves and 30-45 seconds on the
    // cardio finisher, then rest 60-90s before the next round.
    id: "full-body-4", name: "Full-Body Circuit", emoji: "🎯", tag: "push",
    desc: "4 blocks · 3 rounds · 10-15 reps + 30-45s cardio",
    circuits: 3,
    blockLabels: {
      A: "Triceps · Power · Core · Cardio",
      B: "Pull · Posterior · Arms · Cardio",
      C: "Legs · Glutes · Core · Cardio",
      D: "Hamstrings · Shoulders · Core · Cardio",
    },
    exercises_list: [
      { block: "A", id: "cable_tricep",        name: "Tricep Pushdown Machine",      type: "push", muscles: "Triceps",                   defaultWeight: 20, defaultReps: 12 },
      { block: "A", id: "db_clean_press",      name: "DB Clean & Press",             type: "push", muscles: "Shoulders, Back",           defaultWeight: 12, defaultReps: 12 },
      { block: "A", id: "bw_bench_situp",      name: "Bench Sit-Ups",                type: "pull", muscles: "Core",                      defaultWeight: 0,  defaultReps: 12 },
      { block: "A", id: "bw_jump_squat",       name: "Jump Squat",                   type: "legs", muscles: "Quads, Glutes, Cardio",     defaultWeight: 0,  defaultReps: 30 },
      { block: "B", id: "lat_pulldown",        name: "Lat Pulldown",                 type: "pull", muscles: "Back, Biceps",              defaultWeight: 40, defaultReps: 12 },
      { block: "B", id: "db_sldl_upright_row", name: "DB Stiff-Leg DL + Upright Row",type: "pull", muscles: "Hamstrings, Shoulders, Back",defaultWeight: 12, defaultReps: 12 },
      { block: "B", id: "db_curl",             name: "DB Bicep Curls",               type: "pull", muscles: "Biceps",                    defaultWeight: 10, defaultReps: 12 },
      { block: "B", id: "bw_wide_squat_taps",  name: "Wide Squat Narrow Taps",       type: "legs", muscles: "Legs, Cardio",              defaultWeight: 0,  defaultReps: 30 },
      { block: "C", id: "leg_extension",       name: "Leg Extension Machine",        type: "legs", muscles: "Quads",                     defaultWeight: 30, defaultReps: 12 },
      { block: "C", id: "db_back_lunge",       name: "DB Backward Lunges",           type: "legs", muscles: "Quads, Glutes",             defaultWeight: 10, defaultReps: 12 },
      { block: "C", id: "bw_bench_leg_raise",  name: "Bench Leg Raises",             type: "pull", muscles: "Core",                      defaultWeight: 0,  defaultReps: 12 },
      { block: "C", id: "bw_star_jump",        name: "Star Jumps",                   type: "legs", muscles: "Full body, Cardio",         defaultWeight: 0,  defaultReps: 30 },
      { block: "D", id: "leg_curl",            name: "Hamstring Curl Machine",       type: "legs", muscles: "Hamstrings",                defaultWeight: 30, defaultReps: 12 },
      { block: "D", id: "db_lateral_raise",    name: "DB Lateral Raises",            type: "push", muscles: "Shoulders",                 defaultWeight: 6,  defaultReps: 12 },
      { block: "D", id: "db_russian_twist",    name: "DB Russian Twists",            type: "pull", muscles: "Core, Obliques",            defaultWeight: 5,  defaultReps: 12 },
      { block: "D", id: "bw_mountain_climb",   name: "Mountain Climbers",            type: "pull", muscles: "Core, Cardio",              defaultWeight: 0,  defaultReps: 30 },
    ],
    cardio: null,
  },
];

// Pickable list when adding an exercise mid-session.
export const RIYADAH_PICKER_EXERCISES = [
  // Pull
  { id: "lat_pulldown",         name: "Lat Pulldown",            type: "pull",   muscles: "Back, Biceps" },
  { id: "lat_pulldown_o",       name: "Lat Pulldown (Overhand Wide)", type: "pull", muscles: "Lats, Back, Rear delts" },
  { id: "lat_pulldown_h",       name: "Lat Pulldown (Horiz.)",   type: "pull",   muscles: "Back, Biceps" },
  { id: "seated_row",           name: "Cable Row",               type: "pull",   muscles: "Back, Biceps" },
  { id: "seated_row_v",         name: "Seated Row (Vert.)",      type: "pull",   muscles: "Back, Biceps" },
  { id: "db_bent_row",          name: "DB Bent-Over Row",        type: "pull",   muscles: "Back, Biceps" },
  { id: "cable_rope_pullover",  name: "Cable Rope Pullover",     type: "pull",   muscles: "Lats, Back" },
  { id: "cable_face_pull",      name: "Cable Face Pull",         type: "pull",   muscles: "Rear delts, Back" },
  { id: "assisted_pullup",      name: "Assisted Pull Ups",       type: "pull",   muscles: "Back, Biceps" },
  { id: "db_curl",              name: "DB Bicep Curls",           type: "pull",   muscles: "Biceps" },
  { id: "db_rdl",               name: "Romanian Deadlift",       type: "pull",   muscles: "Hamstrings, Glutes" },
  { id: "db_sldl",              name: "DB Stiff-Legged Deadlift",type: "pull",   muscles: "Hamstrings, Glutes" },
  { id: "db_sldl_upright_row",  name: "DB Stiff-Leg DL + Upright Row", type: "pull", muscles: "Hamstrings, Shoulders, Back" },
  // Push — chest
  { id: "chest_press_machine",  name: "Seated Chest Press",      type: "push",   muscles: "Chest, Triceps" },
  { id: "db_bench",             name: "Flat DB Chest Press",     type: "push",   muscles: "Chest, Triceps" },
  { id: "db_incline_bench",     name: "Incline DB Press",        type: "push",   muscles: "Chest, Shoulders" },
  { id: "db_incline_fly",       name: "Incline DB Fly",          type: "push",   muscles: "Chest" },
  { id: "db_fly",               name: "DB Chest Fly",            type: "push",   muscles: "Chest" },
  { id: "bb_bench",             name: "Flat Barbell Bench",      type: "push",   muscles: "Chest, Triceps" },
  { id: "bw_dip",               name: "BW Dips",                 type: "push",   muscles: "Chest, Triceps" },
  // Push — shoulders / arms
  { id: "db_shoulder_press",    name: "Seated DB Shoulder Press",type: "push",   muscles: "Shoulders" },
  { id: "arnold_press",         name: "Arnold Press",            type: "push",   muscles: "Shoulders, Triceps" },
  { id: "db_lateral_raise",     name: "DB Lateral Raises",        type: "push",   muscles: "Shoulders" },
  { id: "db_bent_lateral",      name: "Bent-Over DB Lateral Raise", type: "push", muscles: "Rear delts, Back" },
  { id: "db_front_raise",       name: "DB Front Raise",          type: "push",   muscles: "Shoulders" },
  { id: "db_clean_press",       name: "DB Clean & Press",        type: "push",   muscles: "Shoulders, Back" },
  { id: "db_sumo_clean_press",  name: "DB Sumo Clean & Press",   type: "push",   muscles: "Full body" },
  { id: "cable_tricep",         name: "Tricep Pushdown Machine",   type: "push",   muscles: "Triceps" },
  { id: "db_oh_tricep",         name: "DB Overhead Tricep Ext.", type: "push",   muscles: "Triceps" },
  // Legs
  { id: "leg_press",            name: "Leg Press",               type: "legs",   muscles: "Quads, Glutes" },
  { id: "leg_extension",        name: "Leg Extension Machine",          type: "legs",   muscles: "Quads" },
  { id: "db_goblet_squat",      name: "Goblet Squat",            type: "legs",   muscles: "Quads, Glutes" },
  { id: "db_sumo_squat",        name: "DB Sumo Squat",           type: "legs",   muscles: "Quads, Glutes" },
  { id: "bw_squat",             name: "BW Squats",               type: "legs",   muscles: "Quads, Glutes" },
  { id: "bw_back_lunge",        name: "BW Backward Lunges",      type: "legs",   muscles: "Quads, Glutes" },
  { id: "db_back_lunge",        name: "DB Backward Lunges",      type: "legs",   muscles: "Quads, Glutes" },
  { id: "bw_jump_squat",        name: "Jump Squat",              type: "legs",   muscles: "Quads, Glutes, Cardio" },
  { id: "bw_wide_squat_taps",   name: "Wide Squat Narrow Taps",  type: "legs",   muscles: "Legs, Cardio" },
  { id: "bw_star_jump",         name: "Star Jumps",              type: "legs",   muscles: "Full body, Cardio" },
  { id: "db_lunge",             name: "Walking Lunges",          type: "legs",   muscles: "Quads, Glutes" },
  { id: "db_side_lunge",        name: "DB Side Lunge",           type: "legs",   muscles: "Quads, Glutes, Adductors" },
  { id: "abductor_machine",     name: "Abductor Machine",        type: "legs",   muscles: "Glutes, Abductors" },
  { id: "hip_thrust_machine",   name: "Hip Thrust",              type: "legs",   muscles: "Glutes" },
  // Core / accessory
  { id: "ab_crunch_machine",    name: "Ab Crunch",               type: "pull",   muscles: "Core" },
  { id: "bw_leg_raise",         name: "Leg Raises",              type: "pull",   muscles: "Core" },
  { id: "bw_side_crunch",       name: "Side Crunches",           type: "pull",   muscles: "Core, Obliques" },
  { id: "bw_bicycle_crunch",    name: "Bicycle Crunches",        type: "pull",   muscles: "Core, Obliques" },
  { id: "bw_plank",             name: "Plank",                   type: "pull",   muscles: "Core" },
  { id: "bw_side_plank",        name: "Side Plank",              type: "pull",   muscles: "Core, Obliques" },
  { id: "bw_plank_taps",        name: "Plank Shoulder Taps",     type: "pull",   muscles: "Core, Shoulders" },
  { id: "bw_mountain_climb",    name: "Mountain Climbers",       type: "pull",   muscles: "Core, Cardio" },
  { id: "bw_bench_situp",       name: "Bench Sit-Ups",           type: "pull",   muscles: "Core" },
  { id: "bw_bench_leg_raise",   name: "Bench Leg Raises",        type: "pull",   muscles: "Core" },
  { id: "db_russian_twist",     name: "DB Russian Twists",       type: "pull",   muscles: "Core, Obliques" },
  { id: "back_extension",       name: "Hyperextensions",         type: "pull",   muscles: "Lower back" },
  // Cardio
  { id: "treadmill_walk",       name: "Incline Walk",            type: "cardio", muscles: "Cardio" },
  // Technogym machines
  { id: "tg_chest_press",       name: "Chest Press (Technogym)",          type: "push",  muscles: "Chest, Triceps" },
  { id: "tg_pectoral",          name: "Pectoral Machine (Technogym)",     type: "push",  muscles: "Chest" },
  { id: "tg_shoulder_press",    name: "Shoulder Press (Technogym)",       type: "push",  muscles: "Shoulders, Triceps" },
  { id: "tg_lateral_raise",     name: "Lateral Raise (Technogym)",        type: "push",  muscles: "Shoulders" },
  { id: "tg_lat_machine",       name: "Lat Machine (Technogym)",          type: "pull",  muscles: "Back, Biceps" },
  { id: "tg_vertical_traction", name: "Vertical Traction (Technogym)",    type: "pull",  muscles: "Back, Biceps" },
  { id: "tg_low_row",           name: "Low Row (Technogym)",              type: "pull",  muscles: "Back, Biceps" },
  { id: "tg_pulldown",          name: "Pulldown (Technogym)",             type: "pull",  muscles: "Back, Biceps" },
  { id: "tg_rear_delt",         name: "Rear Delt (Technogym)",            type: "pull",  muscles: "Shoulders, Back" },
  { id: "tg_arm_curl",          name: "Arm Curl (Technogym)",             type: "pull",  muscles: "Biceps" },
  { id: "tg_arm_extension",     name: "Arm Extension (Technogym)",        type: "push",  muscles: "Triceps" },
  { id: "tg_leg_press",         name: "Leg Press (Technogym)",            type: "legs",  muscles: "Quads, Glutes" },
  { id: "tg_leg_extension",     name: "Leg Extension (Technogym)",        type: "legs",  muscles: "Quads" },
  { id: "tg_leg_curl",          name: "Leg Curl (Technogym)",             type: "legs",  muscles: "Hamstrings" },
  { id: "tg_glute",             name: "Glute Machine (Technogym)",        type: "legs",  muscles: "Glutes" },
  { id: "tg_abductor",          name: "Abductor (Technogym)",             type: "legs",  muscles: "Glutes" },
  { id: "tg_adductor",          name: "Adductor (Technogym)",             type: "legs",  muscles: "Adductors" },
  { id: "tg_total_abdominal",   name: "Total Abdominal (Technogym)",      type: "pull",  muscles: "Core" },
  { id: "tg_lower_back",        name: "Lower Back (Technogym)",           type: "pull",  muscles: "Lower back" },
  { id: "tg_calf",              name: "Calf Machine (Technogym)",         type: "legs",  muscles: "Calves" },
  { id: "tg_kinesis_chest",     name: "Kinesis Chest (Technogym)",        type: "push",  muscles: "Chest" },
  { id: "tg_kinesis_pull",      name: "Kinesis Pull (Technogym)",         type: "pull",  muscles: "Back" },
  { id: "tg_smith_squat",       name: "Multipower Squat (Technogym)",     type: "legs",  muscles: "Quads, Glutes" },
  { id: "tg_skillmill",         name: "Skillmill (Technogym)",            type: "cardio", muscles: "Cardio, Legs" },
  { id: "tg_skillrun",          name: "Skillrun (Technogym)",             type: "cardio", muscles: "Cardio" },
  { id: "tg_skillrow",          name: "Skillrow (Technogym)",             type: "cardio", muscles: "Cardio, Back" },
  { id: "tg_skillbike",         name: "Skillbike (Technogym)",            type: "cardio", muscles: "Cardio, Legs" },
];

// ─── Workout history (linear log used by the Hub → Pick → Active flow) ──────

const HISTORY_KEY = "sirat-workouts";

export function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); } catch { return []; }
}
export function saveHistory(h) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(h)); } catch {}
}

// ─── Active session (in-progress workout) ─────────────────────────────────
// Persist the live ActiveScreen so leaving Riyadah / closing the app /
// being WebView-reaped on iOS doesn't lose the workout. On next mount the
// Riyadah parent looks for a stored session and resumes straight back into
// it. Stale sessions (>24h since last touch) are dropped — coming back to
// a half-logged workout from yesterday would feel broken.

const ACTIVE_KEY = "sirat-riyadah-active";
const ACTIVE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function loadActiveSession() {
  try {
    const raw = JSON.parse(localStorage.getItem(ACTIVE_KEY) || "null");
    if (!raw || !raw.template || !Array.isArray(raw.exercises)) return null;
    const lastTouched = Number(raw.lastTouched) || 0;
    if (Date.now() - lastTouched > ACTIVE_MAX_AGE_MS) return null;
    return raw;
  } catch { return null; }
}
export function saveActiveSession(session) {
  try { localStorage.setItem(ACTIVE_KEY, JSON.stringify({ ...session, lastTouched: Date.now() })); } catch {}
}
export function clearActiveSession() {
  try { localStorage.removeItem(ACTIVE_KEY); } catch {}
}

// ─── Custom routines ───────────────────────────────────────────────────────
// User-built workouts persisted to localStorage. Same shape as built-in
// RIYADAH_TEMPLATES entries (so buildWorkout works unchanged) plus a
// `custom: true` marker. Each exercise carries id/name/type/muscles plus
// a `block` letter — kept simple: every custom routine uses block "A".

const ROUTINES_KEY = "sirat-riyadah-routines";

export function loadRoutines() {
  try { return JSON.parse(localStorage.getItem(ROUTINES_KEY) || "[]"); } catch { return []; }
}
export function saveRoutines(rs) {
  try { localStorage.setItem(ROUTINES_KEY, JSON.stringify(rs)); } catch {}
}
export function upsertRoutine(routine) {
  const list = loadRoutines();
  const i = list.findIndex(r => r.id === routine.id);
  if (i >= 0) list[i] = routine; else list.push(routine);
  saveRoutines(list);
  return list;
}
export function deleteRoutine(id) {
  const list = loadRoutines().filter(r => r.id !== id);
  saveRoutines(list);
  return list;
}
export function newRoutineId() {
  return `custom-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
}

// Built-in templates can be hidden from the Hub by id. We don't delete the
// constant — we just persist a list of suppressed ids so the user can
// "delete" a built-in routine and bring it back later via a reset.
const HIDDEN_TEMPLATES_KEY = "sirat-riyadah-hidden-templates";
export function loadHiddenTemplateIds() {
  try { return JSON.parse(localStorage.getItem(HIDDEN_TEMPLATES_KEY) || "[]"); } catch { return []; }
}
export function hideTemplate(id) {
  if (!id || id === "quick-start") return;
  const cur = loadHiddenTemplateIds();
  if (!cur.includes(id)) cur.push(id);
  try { localStorage.setItem(HIDDEN_TEMPLATES_KEY, JSON.stringify(cur)); } catch {}
  return cur;
}
export function unhideTemplate(id) {
  const cur = loadHiddenTemplateIds().filter(x => x !== id);
  try { localStorage.setItem(HIDDEN_TEMPLATES_KEY, JSON.stringify(cur)); } catch {}
  return cur;
}

// Build a custom routine snapshot from an in-progress session's exercises
// list. Used when the user taps "Save as template" inside the Active
// screen (esp. handy for Quick Start sessions built from scratch).
export function snapshotSessionAsRoutine(name, exercises) {
  const id = newRoutineId();
  const dedupedExercises = (exercises || []).filter(ex => ex && ex.exerciseId).map(ex => {
    const def = EXERCISE_BY_ID[ex.exerciseId] || {};
    const firstSet = (ex.sets || [])[0] || {};
    return {
      block: "A",
      id: ex.exerciseId,
      name: ex.name || def.name || ex.exerciseId,
      type: def.category === "Cardio" ? "cardio"
          : (def.primary === "Back" || def.primary === "Biceps") ? "pull"
          : (def.primary === "Legs" || def.primary === "Glutes") ? "legs"
          : "push",
      muscles: [def.primary, ...(def.secondary || [])].filter(Boolean).join(", "),
      defaultWeight: typeof firstSet.weight === "number" ? firstSet.weight : (def.defaults?.weight ?? 0),
      defaultReps: firstSet.reps || def.defaults?.reps || 10,
    };
  });
  return {
    id,
    name: (name || "My Routine").trim().slice(0, 60),
    emoji: "💪",
    tag: "push",
    desc: `${dedupedExercises.length} exercises · custom`,
    circuits: Math.max(1, (exercises?.[0]?.sets?.length) || 3),
    blockLabels: { A: "Custom" },
    exercises_list: dedupedExercises,
    cardio: null,
    custom: true,
  };
}

// Build a map of exerciseName → { weight, reps } from the most recent session
// containing each exercise. Used to ghost-fill the Active screen with last
// session's numbers so the user has visible targets to beat.
export function loadLastExerciseData() {
  // PR target lookup for the Active screen's ghost cells. Returns the
  // ALL-TIME heaviest set per exercise — keyed by both the literal
  // ex.name (legacy) and the resolved canonical name so a workout that
  // logs "Dumbbell Goblet Squats" still sees the PR set under "DB
  // Goblet Squat" elsewhere. Lazy-require to dodge the circular import.
  let resolveExerciseName;
  try { resolveExerciseName = require("./exerciseCanonical").resolveExerciseName; } catch {}
  const history = loadHistory();
  // First pass — find the all-time BEST set per canonical name.
  const bestByCanon = new Map();
  for (const w of history) {
    if (!w.exercises) continue;
    for (const ex of w.exercises) {
      if (!ex.name || !ex.sets || ex.sets.length === 0) continue;
      const canon = resolveExerciseName ? resolveExerciseName(ex.name, ex.id) : ex.name;
      const top = ex.sets.reduce((b, s) => {
        const bw = parseFloat(b.weight) || 0;
        const sw = parseFloat(s.weight) || 0;
        return sw > bw ? s : b;
      }, ex.sets[0]);
      const cur = bestByCanon.get(canon);
      const tw = parseFloat(top.weight) || 0;
      if (!cur || tw > (parseFloat(cur.weight) || 0)) {
        bestByCanon.set(canon, { weight: top.weight, reps: top.reps });
      }
    }
  }
  // Second pass — emit a map keyed by every NAME variant ever logged,
  // each pointing at the all-time best for that canonical exercise.
  // The active session reads by ex.name; this guarantees the PR target
  // is the user's true best regardless of which spelling the current
  // session is using.
  const map = {};
  for (const w of history) {
    if (!w.exercises) continue;
    for (const ex of w.exercises) {
      if (!ex.name) continue;
      const canon = resolveExerciseName ? resolveExerciseName(ex.name, ex.id) : ex.name;
      const best = bestByCanon.get(canon);
      if (best && !map[ex.name]) map[ex.name] = best;
    }
  }
  return map;
}

// Working volume / set counts EXCLUDE warm-up sets (set.warmup) — warm-ups
// are ramp work, not training stimulus, so they never inflate volume or the
// "N sets done" target.
export function calcVolumeFlat(exercises) {
  return exercises.reduce((tot, ex) => tot + ex.sets.reduce(
    (s, set) => s + (set.done && !set.warmup ? (Number(set.weight) || 0) * (Number(set.reps) || 0) : 0), 0), 0);
}
export function calcSetsFlat(exercises) {
  return exercises.reduce((t, ex) => t + ex.sets.filter(s => s.done && !s.warmup).length, 0);
}

export function extractMuscleGroups(workout, max = 5) {
  const counts = new Map();
  for (const ex of (workout.exercises || [])) {
    const setN = (ex.sets || []).length;
    if (setN === 0) continue;
    const muscles = String(ex.muscles || "").split(",").map(s => s.trim()).filter(Boolean);
    for (const m of muscles) counts.set(m, (counts.get(m) || 0) + setN);
  }
  return Array.from(counts.entries()).sort((a,b) => b[1] - a[1]).slice(0, max).map(([m]) => m);
}

// Pre-fill exercises from last session if available, fall back to template defaults.
// Progressive-overload heuristic. Bumps weight when the user has a numeric
// load to work with; for bodyweight or 0kg, suggests +1 rep instead.
// Assisted (negative kg) reduces assistance by 5kg per session.
export function suggestNext(lastWeight, lastReps) {
  if (lastWeight == null) return null;
  const r = parseInt(lastReps, 10) || 0;
  if (lastWeight === "BW" || lastWeight === "0" || lastWeight === 0) {
    return { weight: "BW", reps: String(Math.max(1, r + 1)), delta: "+1 rep" };
  }
  const w = parseFloat(lastWeight);
  if (!isFinite(w)) return null;
  if (w < 0) {
    const next = Math.min(0, w + 5);
    return { weight: String(next), reps: String(r), delta: "−5kg assist" };
  }
  const inc = w >= 20 ? 2.5 : 1;
  const next = Math.round((w + inc) * 10) / 10;
  return { weight: String(next), reps: String(r), delta: `+${inc}kg` };
}

// ─── Progressive-overload engine (feeds the AI Workout Generator) ─────────
// Most-recent SESSION performance per exercise — NOT the all-time PR that
// loadLastExerciseData() returns. Double progression needs to know what the
// user did *last time*, not their best ever. Keyed by both exercise id and
// name so a lookup works whichever the generator has. Per key returns:
//   { weight, reps, minReps, setsDone, daysAgo, date }
// `weight`/`reps` come from the heaviest working set that session; `minReps`
// is the lowest rep count across that session's working sets (so we can tell
// whether ANY set fell short of the target rep range).
export function loadLastSessionPerformance() {
  const history = loadHistory(); // newest-first by convention
  const now = Date.now();
  const out = {};
  const consider = (key, rec) => { if (key && out[key] == null) out[key] = rec; };
  for (const w of history) {
    const ts = w.timestamp || (w.date ? Date.parse(w.date) : 0) || now;
    for (const ex of (w.exercises || [])) {
      // Working sets only — warm-ups don't represent the top load/effort.
      const sets = (ex.sets || []).filter(s => s && !s.warmup && (parseInt(s.reps, 10) || 0) > 0);
      if (!sets.length) continue;
      const top = sets.reduce((b, s) =>
        (parseFloat(s.weight) || 0) > (parseFloat(b.weight) || 0) ? s : b, sets[0]);
      const repsList = sets.map(s => parseInt(s.reps, 10) || 0);
      const rec = {
        weight: parseFloat(top.weight) || 0,
        reps: parseInt(top.reps, 10) || 0,
        minReps: Math.min(...repsList),
        setsDone: sets.length,
        daysAgo: Math.max(0, Math.round((now - ts) / 86400000)),
        date: w.date || null,
        // RPE (1–10) drives RPE-autoregulated progression downstream. Prefer
        // this exercise's own rating; fall back to the session-level RPE.
        sessionRPE: typeof ex.rpe === "number" ? ex.rpe
                  : typeof w.sessionRPE === "number" ? w.sessionRPE : null,
      };
      consider(ex.id, rec);
      consider(ex.name, rec);
    }
  }
  return out;
}

// Double progression, RPE-autoregulated. Given last session's performance for
// an exercise and its library definition, decide the next working load.
//
// Rep-based base decision:
//   • all working sets hit the TOP of the rep range  → INCREASE (≈+2.5–5%,
//     rounded to a realistic plate/DB step)
//   • reps fell a little short (within 2 of target)   → HOLD (repeat the
//     weight, chase the missing reps)
//   • clear grind (≥3 reps short, or only one set)    → DELOAD (≈−7%)
//
// RPE override (last session's logged session RPE, 1–10):
//   • RPE ≥ 9   → an "increase" is downgraded to HOLD (they hit the reps but
//     were already maxed — consolidate before adding load).
//   • RPE ≥ 9.5 → a "hold" is downgraded to DELOAD (back off).
//   • RPE ≤ 6   → a borderline "hold" (within 1 rep of target) is upgraded to
//     INCREASE (reps were in reserve, safe to push).
//
// Bodyweight progresses by reps; assisted machines by reducing assistance;
// duration-based moves return null. Returns
// { suggestedKg, suggestedReps, action, note, lastWeight, lastReps, lastRPE }
// or null. This is the target for a NORMAL (HARD/MEDIUM) day — readiness
// scaling (EASY → 85–90%, no PRs) is applied downstream by the volume rules.
export function progressionTarget(last, exDef) {
  if (!last) return null;
  const unit = exDef?.unit || "kg";
  if (unit === "min") return null; // cardio / planks — not load-progressed
  const targetReps = exDef?.defaults?.reps ?? 10;
  const minReps = last.minReps ?? last.reps ?? 0;
  const hitTop = minReps >= targetReps && last.setsDone >= 2;
  const grind  = last.setsDone <= 1 || (targetReps - minReps) >= 3;
  const rpe = typeof last.sessionRPE === "number" ? last.sessionRPE : null;

  // Base action from reps, then autoregulate by RPE.
  let action = hitTop ? "increase" : grind ? "deload" : "hold";
  let rpeNote = null;
  if (rpe != null) {
    if (rpe >= 9 && action === "increase") { action = "hold"; rpeNote = `RPE ${rpe} — hit reps but maxed, consolidate`; }
    else if (rpe >= 9.5 && action === "hold") { action = "deload"; rpeNote = `RPE ${rpe} — back off`; }
    else if (rpe <= 6 && action === "hold" && (targetReps - minReps) <= 1) { action = "increase"; rpeNote = `RPE ${rpe} — reps in reserve, push`; }
  }

  const mk = (suggestedKg, suggestedReps, note) =>
    ({ suggestedKg, suggestedReps, action, note: rpeNote || note, lastWeight: last.weight, lastReps: last.reps, lastRPE: rpe });

  if (unit === "reps") { // bodyweight — progress reps, never load
    if (action === "increase") return mk("BW", minReps + 1, "+1 rep");
    if (action === "deload")   return mk("BW", Math.max(1, targetReps - 2), "ease off");
    return mk("BW", targetReps, "hold, chase reps");
  }

  if (unit === "kg_assist") { // negative weight = kg of assistance
    const w = last.weight;
    if (action === "increase") return mk(Math.min(0, w + 5), last.reps, "−5kg assist");
    if (action === "deload")   return mk(w - 5, last.reps, "+5kg assist");
    return mk(w, last.reps, "hold assist");
  }

  const w = last.weight;
  if (!(w > 0)) return null;
  const step = w >= 60 ? 5 : w >= 20 ? 2.5 : w >= 10 ? 2 : 1; // ≈2.5–5% across the user's load ranges
  if (action === "increase") return mk(Math.round((w + step) * 2) / 2, targetReps, `+${step}kg`);
  if (action === "deload")   return mk(Math.max(step, Math.round((w * 0.93) * 2) / 2), targetReps, "≈−7%");
  return mk(w, targetReps, "repeat, chase reps");
}

export function buildWorkout(template, lastData = {}) {
  const circuits = template.circuits || 2;
  const exercises = (template.exercises_list || []).map((ex, idx) => {
    const last = lastData[ex.name];
    const w = last ? last.weight : (ex.defaultWeight > 0 ? String(ex.defaultWeight) : "BW");
    const r = last ? last.reps   : String(ex.defaultReps);
    const suggestion = last ? suggestNext(last.weight, last.reps) : null;
    return {
      ...ex,
      uid: `${ex.id}-${idx}`,
      lastWeight: last ? last.weight : null,
      lastReps:   last ? last.reps   : null,
      suggestion,
      sets: Array.from({ length: circuits }, (_, i) => ({
        id: `${ex.id}-${idx}-s${i}`,
        weight: w, reps: r, done: false,
      })),
    };
  });
  return { exercises, cardio: template.cardio ? { ...template.cardio, done: false } : null };
}

// ─── Goals storage ───────────────────────────────────────────────────────
const GOALS_KEY = "sirat-goals";
export function loadGoals() {
  try { return JSON.parse(localStorage.getItem(GOALS_KEY) || "[]"); } catch { return []; }
}
export function saveGoals(g) {
  try { localStorage.setItem(GOALS_KEY, JSON.stringify(g)); } catch {}
}

// ─── Calendar streak helpers (workout days, gap up to 3 rest days) ──────
// Workout-day cadence — Mon/Wed/Fri counts. Gap of >3 rest days breaks
// the streak. Uses local-date strings to match the displayed day.
function dayKey(ts) {
  const d = new Date(ts);
  return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
}
function daysBetween(aKey, bKey) {
  const [ay, am, ad] = aKey.split("-").map(Number);
  const [by, bm, bd] = bKey.split("-").map(Number);
  const a = new Date(ay, am - 1, ad);
  const b = new Date(by, bm - 1, bd);
  return Math.round((b - a) / 86400000);
}
export function computeWorkoutStreaks(history) {
  if (!history || history.length === 0) return { current: 0, longest: 0 };
  const days = Array.from(new Set(history.filter(w => w.timestamp).map(w => dayKey(w.timestamp)))).sort();
  if (days.length === 0) return { current: 0, longest: 0 };
  let longest = 1, run = 1;
  for (let i = 1; i < days.length; i++) {
    const gap = daysBetween(days[i - 1], days[i]);
    if (gap >= 1 && gap <= 4) run += 1; // gap 1 = next day, 4 = 3 rest days between
    else run = 1;
    if (run > longest) longest = run;
  }
  // Current streak: walk backward from today (or last workout if recent enough).
  const todayKey = dayKey(Date.now());
  const lastKey = days[days.length - 1];
  const gapToToday = daysBetween(lastKey, todayKey);
  if (gapToToday > 4) return { current: 0, longest };
  let current = 1;
  for (let i = days.length - 1; i > 0; i--) {
    const gap = daysBetween(days[i - 1], days[i]);
    if (gap >= 1 && gap <= 4) current += 1; else break;
  }
  return { current, longest };
}

// ─── PR detection (weight OR reps-at-weight beats prior best) ───────────
// Returns true if the candidate set is a PR for `exName` given prior history
// (excluding the session that contains the candidate). priorHistory should be
// chronologically anything older than `set`.
export function isPRSet(set, exName, priorHistory) {
  const cw = parseFloat(set.weight) || 0;
  const cr = parseInt(set.reps, 10) || 0;
  if (cw <= 0 || cr <= 0) return false;
  let bestWeight = 0;
  let bestRepsAtCw = 0;
  for (const w of priorHistory) {
    for (const ex of (w.exercises || [])) {
      if (ex.name !== exName) continue;
      for (const s of (ex.sets || [])) {
        const sw = parseFloat(s.weight) || 0;
        const sr = parseInt(s.reps, 10) || 0;
        if (sw > bestWeight) bestWeight = sw;
        if (sw === cw && sr > bestRepsAtCw) bestRepsAtCw = sr;
      }
    }
  }
  if (cw > bestWeight) return true;
  if (cw === bestWeight && cr > bestRepsAtCw) return true;
  return false;
}

// ─── Coach → Exercise bridge ──────────────────────────────────────────────
// The AI Coach can build a workout and hand it to the Exercise tab. The
// pending workout (a ready template) lives under this key; Riyadah surfaces a
// "Start" card on the Hub and runs it through the normal Active flow.
const COACH_WORKOUT_KEY = "hayahCoachWorkout";
export function loadCoachWorkout() {
  try { return JSON.parse(localStorage.getItem(COACH_WORKOUT_KEY) || "null"); } catch { return null; }
}
export function saveCoachWorkout(template) {
  try {
    localStorage.setItem(COACH_WORKOUT_KEY, JSON.stringify(template));
    window.dispatchEvent(new CustomEvent("sirat:coachWorkout"));
  } catch {}
}
export function clearCoachWorkout() {
  try { localStorage.removeItem(COACH_WORKOUT_KEY); } catch {}
}

// Map a coach-built workout { name, note, exercises:[{id,sets,reps,weight}] }
// to a Riyadah template the Active flow runs unchanged. Unknown ids are
// dropped; missing weight/reps fall back to library defaults.
export function coachWorkoutToTemplate(w) {
  if (!w || !Array.isArray(w.exercises)) return null;
  const picks = w.exercises
    .map(e => (EXERCISE_BY_ID[e.id] ? { lib: EXERCISE_BY_ID[e.id], e } : null))
    .filter(Boolean)
    .slice(0, 8);
  if (!picks.length) return null;
  const exercises_list = picks.map(({ lib, e }, i) => {
    const exType =
      lib.category === "Cardio" ? "cardio"
      : (lib.primary === "Back" || lib.primary === "Biceps") ? "pull"
      : (lib.primary === "Legs" || lib.primary === "Glutes") ? "legs"
      : (lib.primary === "Core") ? "core"
      : "push";
    return {
      block: ["A", "B", "C", "D"][Math.floor(i / 3)] || "A",
      id: lib.id,
      name: lib.name,
      muscles: [lib.primary, ...(lib.secondary || [])].join(", "),
      unit: lib.unit || "reps",
      // Time-based moves (planks, stretches, breathing) carry a hold duration
      // so the card + Active flow show "X min" instead of reps.
      defaultDurationMin: lib.unit === "min"
        ? (Number.isFinite(e.durationMin) && e.durationMin > 0 ? e.durationMin : (lib.defaults?.durationMin ?? 1))
        : null,
      // Bodyweight / stretch moves carry no load → 0 (shown as "BW"). For loaded
      // moves, accept only a sane weight; reject NaN / negatives / absurd
      // values (the model has emitted e.g. 1.23e+198) so the Exercise screen
      // never opens with garbage.
      defaultWeight: (lib.category === "Bodyweight" || lib.category === "Stretch") ? 0
        : (Number.isFinite(e.weight) && e.weight >= 0 && e.weight <= 500
            ? Math.round(e.weight * 10) / 10
            : (lib.defaults?.weight ?? 0)),
      defaultReps: Number.isFinite(e.reps) && e.reps > 0 ? e.reps : (lib.defaults?.reps ?? 10),
      type: exType,
    };
  });
  return {
    id: `coach_${Date.now()}`,
    name: String(w.name || "Coach Workout").slice(0, 50),
    emoji: "🤖",
    desc: w.note ? String(w.note).slice(0, 90) : "Built by your AI Coach",
    circuits: 3,
    source: "coach",
    plannedExerciseIds: exercises_list.map(x => x.id),
    exercises_list,
  };
}

// A gentle, bed-friendly wind-down routine for sleep — all lying-down / floor
// stretches + slow breathing, no load. Returns the coach-workout shape that
// coachWorkoutToTemplate() consumes, so it rides the exact same
// "Open in Exercise" pipeline as an AI-built workout. Used by the AI Coach
// (deterministic, no model call) and the Quick Access "Wind Down" tile.
export function windDownWorkout() {
  return {
    name: "Wind-Down for Sleep",
    note: "Gentle bedtime stretches — do them in bed. Breathe slow, hold easy, never strain.",
    exercises: [
      { id: "st_diaphragm_breath", weight: 0 },
      { id: "st_knee_to_chest",    weight: 0 },
      { id: "st_supine_twist",     weight: 0 },
      { id: "st_figure4_supine",   weight: 0 },
      { id: "st_happy_baby",       weight: 0 },
      { id: "st_child_pose",       weight: 0 },
      { id: "st_legs_up_wall",     weight: 0 },
      { id: "st_box_breath",       weight: 0 },
    ],
  };
}

// ─── Seed data ──────────────────────────────────────────────────────────
// Spread ~16 sessions over the past 12 weeks so reports/goals look real on
// first run. Runs once: gated by SEED_FLAG_KEY so re-clearing history won't
// re-seed.
const SEED_FLAG_KEY = "sirat-workouts-seeded";
export function maybeSeedHistory() {
  if (localStorage.getItem(SEED_FLAG_KEY) === "1") return null;
  if (loadHistory().length > 0) { localStorage.setItem(SEED_FLAG_KEY, "1"); return null; }

  // Three rotating routines, gentle progressive overload across 12 weeks.
  const routines = [
    { name: "Circuit A", exercises: [
      { name: "Assisted Pull Ups",  muscles: "Back, Biceps",   type: "pull", baseW: 18, reps: 10 },
      { name: "DB Lateral Raises",  muscles: "Shoulders",      type: "push", baseW: 6,  reps: 12 },
      { name: "DB Curls",           muscles: "Biceps",         type: "pull", baseW: 10, reps: 10 },
    ]},
    { name: "Circuit B", exercises: [
      { name: "Abductor Machine",   muscles: "Glutes",         type: "legs", baseW: 35, reps: 15 },
      { name: "BW Dips",            muscles: "Chest, Triceps", type: "push", baseW: 0,  reps: 8  },
      { name: "Arnold Press",       muscles: "Shoulders",      type: "push", baseW: 10, reps: 10 },
    ]},
    { name: "Full Body", exercises: [
      { name: "Goblet Squat",       muscles: "Legs",           type: "legs", baseW: 16, reps: 12 },
      { name: "DB Bench Press",     muscles: "Chest",          type: "push", baseW: 14, reps: 10 },
      { name: "Cable Row",          muscles: "Back",           type: "pull", baseW: 30, reps: 10 },
      { name: "Shoulder Press",     muscles: "Shoulders",      type: "push", baseW: 12, reps: 10 },
    ]},
  ];

  // Pick 16 dates — Mon/Wed/Fri pattern across 12 weeks, occasionally skip.
  const today = new Date(); today.setHours(18, 30, 0, 0);
  const sessions = [];
  const skipPattern = new Set(["w2-Wed", "w5-Fri", "w8-Mon", "w10-Wed"]);
  let idx = 0;
  for (let week = 11; week >= 0; week--) {
    for (const dow of [1, 3, 5]) { // Mon=1, Wed=3, Fri=5
      const tag = `w${11 - week}-${dow === 1 ? "Mon" : dow === 3 ? "Wed" : "Fri"}`;
      if (skipPattern.has(tag)) continue;
      const d = new Date(today);
      d.setDate(today.getDate() - week * 7 - (today.getDay() - dow));
      if (d > today) continue;
      const routine = routines[idx % routines.length];
      const progress = (12 - week) / 12; // 0 → 1
      const sets = 2;
      const exs = routine.exercises.slice(0, 2 + (idx % 3 === 0 ? 1 : 0));
      const exercises = exs.map(ex => {
        const w = ex.baseW > 0 ? Math.round(ex.baseW + ex.baseW * 0.25 * progress) : 0;
        return {
          name: ex.name, muscles: ex.muscles, type: ex.type,
          sets: Array.from({ length: sets }, () => ({ weight: String(w), reps: String(ex.reps) })),
        };
      });
      const volume = exercises.reduce((tot, ex) =>
        tot + ex.sets.reduce((s, set) => s + (Number(set.weight) || 0) * (Number(set.reps) || 0), 0), 0);
      const totalSets = exercises.reduce((t, ex) => t + ex.sets.length, 0);
      const duration = (40 + Math.floor(Math.random() * 31)) * 60; // 40–70m
      const cardio = idx % 3 === 0 ? { name: "Incline Walk", duration: 15, incline: 8, speed: 5.5, done: true } : null;
      sessions.push({
        name: routine.name,
        date: d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" }),
        time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        timestamp: d.getTime(),
        duration,
        sets: totalSets,
        volume,
        exercises,
        prs: idx % 4 === 0 ? 1 : 0,
        cardio,
      });
      idx += 1;
      if (sessions.length >= 16) break;
    }
    if (sessions.length >= 16) break;
  }
  // Newest first (matches insertion order used elsewhere).
  sessions.sort((a, b) => b.timestamp - a.timestamp);
  saveHistory(sessions);
  localStorage.setItem(SEED_FLAG_KEY, "1");
  return sessions;
}

export function fmtDuration(secs) {
  const m = Math.floor(secs / 60), s = secs % 60;
  const pad = n => n < 10 ? "0" + n : "" + n;
  return `${pad(m)}:${pad(s)}`;
}
export function fmtDurHuman(secs) {
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}
export function todayLabel() {
  return new Date().toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
}
export function nowTimeLabel() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
